import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
    Save, Trash2, Plus, Search, ShoppingBag, CreditCard, Package, X, AlertCircle, CheckCircle, Edit3
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API, { socket } from '../../services/api';

const user = JSON.parse(localStorage.getItem('user') || '{}');
const userId = user.userId || user.id;
const COMPANY_ID = user.companyId || user.company_id || 'CPY-DEFAULT';

const STORAGE_KEY_PANIER = `achats_mp_panier_${COMPANY_ID}_${userId}`;
const STORAGE_KEY_HEADER = `achats_mp_header_${COMPANY_ID}_${userId}`;

const AchatMp = () => {
    const [matieres, setMatieres] = useState([]);
    const [fournisseurs, setFournisseurs] = useState([]); 
    
    const [panier, setPanier] = useState(() => {
        try {
            const savedPanier = localStorage.getItem(STORAGE_KEY_PANIER);
            return savedPanier ? JSON.parse(savedPanier) : [];
        } catch (e) {
            console.error("Erreur lecture localStorage panier:", e);
            return [];
        }
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMp, setSelectedMp] = useState(null);
    const [inputQte, setInputQte] = useState('');
    const [inputMontant, setInputMontant] = useState('');
    const [obs, setObs] = useState('');
    const [isInitialLoad, setIsInitialLoad] = useState(true); 
    const [alert, setAlert] = useState({ show: false, msg: '', type: 'error' });
    const [paymentMethods, setPaymentMethods] = useState([]);
    
    const [editingInterneId, setEditingInterneId] = useState(null);

    const [regimeTVA, setRegimeTVA] = useState(1);
    const [tauxTVA, setTauxTVA] = useState(18);
    const [isTVAApplicable, setIsTVAApplicable] = useState(true);
    const [isManualTax, setIsManualTax] = useState(false);
    const [inputHT, setInputHT] = useState('');
    const [inputTVA, setInputTVA] = useState('');

    const [header, setHeader] = useState(() => {
        try {
            const savedHeader = localStorage.getItem(STORAGE_KEY_HEADER);
            if (savedHeader) {
                return JSON.parse(savedHeader);
            }
        } catch (e) {
            console.error("Erreur lecture localStorage header:", e);
        }
        return {
            numFacture: '',
            fournisseurId: '',
            fournisseurNom: '',
            modeReglement: '',
            montantVersé: 0,
            date: new Date().toISOString().split('T')[0]
        };
    });

    const fmt = (val) => {
        if (val === undefined || val === null || isNaN(val) || val === '') return "0";
        return Number(val).toLocaleString('fr-FR');
    };

    const showAlert = (msg, type = 'error') => {
        setAlert({ show: true, msg, type });
        setTimeout(() => setAlert({ show: false, msg: '', type: 'error' }), 5000);
    };

    const currentLotId = useMemo(() => `LOT-${Date.now().toString().slice(-6)}`, [panier.length === 0]);
    const currentAchatId = useMemo(() => `ACH-${Date.now().toString().slice(-6)}`, [panier.length === 0]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_PANIER, JSON.stringify(panier));
        } catch (e) {
            console.error("Erreur écriture localStorage panier:", e);
        }
    }, [panier]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_HEADER, JSON.stringify(header));
        } catch (e) {
            console.error("Erreur écriture localStorage header:", e);
        }
    }, [header]);

    const fetchData = useCallback(async () => {
        try {
            const [resMp, resSup, resComp, resPay] = await Promise.all([
                API.get('/raw-materials'), 
                API.get('/suppliers'),
                API.get(`/company/${COMPANY_ID}`),
                API.get('/plan-comptable/paiements/methodes')
            ]);
            
            const activesOnly = (resMp.data || []).filter(m => m.is_active === 1);
            setMatieres(activesOnly);
            setFournisseurs(resSup.data || []);
            
            const methods = resPay.data?.data || [];
            setPaymentMethods(methods.filter(m => m.is_active === 1));

            if (resComp.data?.success) {
                setRegimeTVA(resComp.data.data.regime_tva_recuperable);
                setTauxTVA(resComp.data.data.taux_tva_defaut || 18);
            }
        } catch (err) { 
            console.error("Erreur API:", err); 
            showAlert("Impossible de charger les données.");
        }
    }, []);

    const loadPersistedCart = async () => {
        try {
            const res = await API.get('/achats-mp/temp');
            if (res.data && res.data.items && res.data.items.length > 0 && panier.length === 0) {
                setPanier(res.data.items);
            }
        } catch (err) {
            console.error("Erreur panier persistant serveur:", err);
        } finally {
            setIsInitialLoad(false);
        }
    };

    useEffect(() => {
        fetchData();
        loadPersistedCart();

        const handleGlobalUpdate = (event) => {
            const { table, action, userId: triggerUserId } = event.detail;
            const monitoredTables = ['matieres_premieres', 'suppliers', 'purchases_mp', 'all'];
            
            if (monitoredTables.includes(table)) {
                fetchData();
            }

            if (table === 'purchases_mp' && action === 'INSERT' && String(triggerUserId) === String(userId)) {
                setPanier([]);
                localStorage.removeItem(STORAGE_KEY_PANIER);
                localStorage.removeItem(STORAGE_KEY_HEADER);
                console.log("🧹 Panier MP vidé après succès.");
            }
        };

        window.addEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
        return () => window.removeEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
    }, [fetchData, userId]);

    const totalPanier = panier.reduce((acc, curr) => acc + curr.montant_facture_ligne, 0);

    // Calculs pour les totaux du pied de tableau
    const totalQte = panier.reduce((acc, curr) => acc + Number(curr.qte_achetee || 0), 0);
    const totalHT = panier.reduce((acc, curr) => acc + Number(curr.montant_ht_ligne || 0), 0);
    const totalTVA = panier.reduce((acc, curr) => acc + Number(curr.montant_tva_ligne || 0), 0);

    const ajouterLigne = () => {
        const isAmountValid = isManualTax ? (inputHT !== '') : (inputMontant !== '');

        if (!selectedMp || !inputQte || !isAmountValid || !header.fournisseurId || !header.numFacture) {
            showAlert("⚠️ Informations manquantes (Facture, Fournisseur, Article ou Montant).");
            return;
        }

        const qte = Number(inputQte);
        let mtHT, mtTVA, mtTTC;

        if (isManualTax) {
            mtHT = Number(inputHT);
            mtTVA = Number(inputTVA) || 0;
            mtTTC = mtHT + mtTVA;
        } else {
            mtTTC = Number(inputMontant);
            const diviseur = 1 + (tauxTVA / 100);
            const calculTVA = (regimeTVA === 1 && isTVAApplicable);
            mtHT = calculTVA ? (mtTTC / diviseur) : mtTTC;
            mtTVA = mtTTC - mtHT;
        }

        const stkAv = Number(selectedMp.stock_actuel) || 0;
        const cmpAnc = Number(selectedMp.cmp) || 0;
        const puHT = qte > 0 ? mtHT / qte : 0; 

        if (editingInterneId) {
            setPanier(panier.map(item => {
                if (item.id_interne === editingInterneId) {
                    return {
                        ...item,
                        num_facture: header.numFacture,
                        supplier_id: header.fournisseurId,
                        nom_fournisseur_snap: header.fournisseurNom,
                        matiere_id: selectedMp.id,
                        nom_matiere_snap: selectedMp.nom,
                        stock_avant_achat: stkAv,
                        qte_achetee: qte,
                        cmp_ancien: cmpAnc,
                        montant_cmp_ancien: stkAv * cmpAnc,
                        montant_ht_ligne: mtHT,
                        montant_tva_ligne: mtTVA,
                        montant_facture_ligne: mtTTC,
                        montant_avance: header.modeReglement === 'ACOMPTE' ? Number(header.montantVersé) : 0,
                        prix_achat_unitaire: puHT, 
                        mode_reglement: header.modeReglement, 
                        ecart: mtHT - (qte * cmpAnc),
                        observations: isManualTax ? `${obs} (MANUEL)` : (isTVAApplicable ? obs : `${obs} (HORS TVA)`),
                        date: header.date
                    };
                }
                return item;
            }));
            setEditingInterneId(null);
            showAlert("Ligne mise à jour avec succès", "success");
        } else {
            const nouvelleLigne = {
                id_interne: Date.now(),
                id_achat: currentAchatId,
                lot_id: currentLotId,
                num_facture: header.numFacture,
                supplier_id: header.fournisseurId,
                nom_fournisseur_snap: header.fournisseurNom,
                matiere_id: selectedMp.id,
                nom_matiere_snap: selectedMp.nom,
                user_id: userId, 
                company_id: COMPANY_ID, 
                stock_avant_achat: stkAv,
                qte_achetee: qte,
                cmp_ancien: cmpAnc,
                montant_cmp_ancien: stkAv * cmpAnc,
                montant_ht_ligne: mtHT,
                montant_tva_ligne: mtTVA,
                montant_facture_ligne: mtTTC,
                montant_avance: header.modeReglement === 'ACOMPTE' ? Number(header.montantVersé) : 0,
                prix_achat_unitaire: puHT, 
                mode_reglement: header.modeReglement, 
                ecart: mtHT - (qte * cmpAnc),
                observations: isManualTax ? `${obs} (MANUEL)` : (isTVAApplicable ? obs : `${obs} (HORS TVA)`),
                date: header.date
            };
            setPanier([...panier, nouvelleLigne]);
            showAlert("Article ajouté au bordereau", "success");
        }

        setSelectedMp(null); 
        setInputQte(''); 
        setInputMontant(''); 
        setObs('');
        setInputHT(''); 
        setInputTVA(''); 
        setIsTVAApplicable(true);
    };

    const preparerEditionLigne = (item) => {
        setEditingInterneId(item.id_interne);
        const mpTrouvee = matieres.find(m => m.id === item.matiere_id) || { id: item.matiere_id, nom: item.nom_matiere_snap, stock_actuel: item.stock_avant_achat, cmp: item.cmp_ancien };
        setSelectedMp(mpTrouvee);
        setInputQte(item.qte_achetee);
        setInputMontant(item.montant_facture_ligne);
        setInputHT(item.montant_ht_ligne);
        setInputTVA(item.montant_tva_ligne);
        setObs(item.observations ? item.observations.replace(' (MANUEL)', '').replace(' (HORS TVA)', '') : '');
        
        setHeader(prev => ({
            ...prev,
            numFacture: item.num_facture,
            fournisseurId: item.supplier_id,
            fournisseurNom: item.nom_fournisseur_snap,
            modeReglement: item.mode_reglement || prev.modeReglement
        }));
        
        showAlert("Mode édition activé pour la ligne sélectionnée", "success");
    };

    const supprimerLigne = (idInterne) => {
        if (editingInterneId === idInterne) {
            setEditingInterneId(null);
            setSelectedMp(null); setInputQte(''); setInputMontant(''); setObs('');
        }
        setPanier(panier.filter(item => item.id_interne !== idInterne));
        showAlert("Article retiré", "success");
    };

    const handleSavePurchase = async () => {
        if (panier.length === 0) return showAlert("Le panier est vide.");
        
        if (!header.modeReglement) {
            return showAlert("⚠️ Veuillez choisir un mode de règlement.");
        }

        const payload = {
            header: {
                numFacture: header.numFacture,
                fournisseurId: header.fournisseurId,
                fournisseur: header.fournisseurNom,
                modeReglement: header.modeReglement, 
                montantAvance: Number(header.montantVersé) || 0,
                lotId: currentLotId,
                date: header.date,
                company_id: COMPANY_ID,
                user_id: userId
            },
            items: panier.map(item => ({
                id_article: item.matiere_id,
                nom_article: item.nom_matiere_snap,
                qte_achetee: Number(item.qte_achetee),
                montant_facture_ligne: Number(item.montant_facture_ligne),
                montant_ht_ligne: Number(item.montant_ht_ligne),
                montant_tva_ligne: Number(item.montant_tva_ligne),
                stock_avant: Number(item.stock_avant_achat),
                cmp_ancien: Number(item.cmp_ancien),
                ecart: Number(item.ecart),
                observations: item.observations
            }))
        };

        try {
            const res = await API.post('/achats-mp', payload);
            if (res.status === 201) {
                showAlert("✅ Bordereau MP enregistré avec succès !", "success");
                setPanier([]);
                setEditingInterneId(null);
                localStorage.removeItem(STORAGE_KEY_PANIER);
                localStorage.removeItem(STORAGE_KEY_HEADER);
                setHeader({ 
                    ...header, 
                    numFacture: '', 
                    montantVersé: 0, 
                    fournisseurId: '', 
                    fournisseurNom: '',
                    modeReglement: '' 
                });
                fetchData(); 
            }
        } catch (err) {
            showAlert("Erreur: " + (err.response?.data?.error || "Échec de l'enregistrement."));
        }
    };

    return (
        <div style={styles.layout}>
            <Sidebar />
            <main style={styles.main}>
                
               {alert.show && (
                    <div style={styles.alertToastContainer}>
                        <div style={{
                            ...styles.alertToast, 
                            backgroundColor: alert.type === 'success' ? '#ecfdf5' : '#fef2f2',
                            color: alert.type === 'success' ? '#065f46' : '#991b1b',
                            border: `1px solid ${alert.type === 'success' ? '#059669' : '#ef4444'}`
                        }}>
                            {alert.type === 'success' ? <CheckCircle size={18}/> : <AlertCircle size={18}/>}
                            <span style={{flex: 1, fontWeight: '500', fontSize: '14px'}}>{alert.msg}</span>
                            <X size={16} style={{cursor: 'pointer', opacity: 0.7}} onClick={() => setAlert({ ...alert, show: false })}/>
                        </div>
                    </div>
                )}

                <header style={styles.headerBar}>
                    <div>
                        <h1 style={styles.title}>GESTION DES ACHATS MP</h1>
                        <div style={styles.badgeContainer}>
                            <span style={styles.badge}>ID: {currentAchatId}</span>
                            <span style={{...styles.badge, background: '#ecfdf5', color: '#059669', border: '1px solid #10b981'}}>Lot: {currentLotId}</span>
                        </div>
                    </div>
                    <button style={styles.btnSave} onClick={handleSavePurchase}>
                        <Save size={18}/> ENREGISTRER L'ACHAT
                    </button>
                </header>

                <div style={styles.container}>
                    <div style={styles.gridTop}>
                        <section style={styles.card}>
                            <h3 style={styles.cardTitle}><Package size={16} color="#059669"/> Catalogue Matières</h3>
                            <div style={styles.searchContainer}>
                                <Search size={16} style={styles.searchIcon}/>
                                <input style={styles.searchInput} placeholder="Filtrer par nom..." onChange={e => setSearchTerm(e.target.value)} />
                            </div>
                            <div style={styles.listArea}>
                                <div style={styles.listHeader}>
                                    <span style={{ flex: 1 }}>Nom</span>
                                    <span style={{ flex: 1, textAlign: 'center' }}>P.U</span>
                                    <span style={{ flex: 1, textAlign: 'right' }}>Stock</span>
                                </div>

                                {matieres.filter(m => m.nom.toLowerCase().includes(searchTerm.toLowerCase())).map(m => (
                                    <div 
                                        key={m.id} 
                                        style={{
                                            ...styles.listItem, 
                                            borderLeft: selectedMp?.id === m.id ? '4px solid #059669' : '4px solid transparent', 
                                            background: selectedMp?.id === m.id ? '#f0fdf4' : 'white'
                                        }} 
                                        onClick={() => setSelectedMp(m)}
                                    >
                                        <span style={{ flex: 1, fontWeight: selectedMp?.id === m.id ? 'bold' : 'normal' }}>
                                            {m.nom}
                                        </span>
                                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                            <span style={styles.puLabel}>{fmt(m.cmp)} F</span>
                                        </div>
                                        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                            <span style={styles.stockLabel}>{m.stock_actuel} {m.unite_mesure}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section style={{...styles.card, borderTop: '4px solid #059669'}}>
                            <h3 style={styles.cardTitle}>
                                <CreditCard size={16} color="#059669"/> 
                                {editingInterneId ? "✏️ MODE MODIFICATION LIGNE" : "Détails de la Facture & Paiement"}
                            </h3>
                            <div style={styles.formRow}>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>N° Facture</label>
                                    <input style={styles.input} value={header.numFacture} onChange={e => setHeader({...header, numFacture: e.target.value})} placeholder="Ex: FAC-2024-001"/>
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Fournisseur</label>
                                    <select style={styles.input} value={header.fournisseurId} onChange={e => {
                                        const f = fournisseurs.find(x => x.id == e.target.value);
                                        setHeader({...header, fournisseurId: e.target.value, fournisseurNom: f?.nom});
                                    }}>
                                        <option value="">Sélectionner fournisseur...</option>
                                        {fournisseurs.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div style={{...styles.selectionBox, borderColor: editingInterneId ? '#3b82f6' : '#059669', background: editingInterneId ? '#eff6ff' : '#f0fdf4'}}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    {selectedMp ? (
                                        <p style={{ margin: 0 }}>
                                            Saisie pour : <strong style={{ color: editingInterneId ? '#3b82f6' : '#059669' }}>{selectedMp.nom}</strong> | <small>CMP: {fmt(selectedMp.cmp)} F</small>
                                        </p>
                                    ) : <p style={{ margin: 0, color: '#94a3b8' }}>Veuillez cliquer sur une matière à gauche</p>}

                                    {regimeTVA === 1 && (
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            {!isManualTax && (
                                                <button onClick={() => setIsTVAApplicable(!isTVAApplicable)} style={{ fontSize: '10px', fontWeight: '800', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', background: isTVAApplicable ? '#dcfce7' : '#fee2e2', color: isTVAApplicable ? '#166534' : '#991b1b' }}>
                                                    {isTVAApplicable ? "TVA ACTIVE" : "HORS TVA"}
                                                </button>
                                            )}
                                            <button onClick={() => setIsManualTax(!isManualTax)} style={{ fontSize: '10px', background: '#1e293b', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}>
                                                {isManualTax ? "🤖 MODE AUTO" : "📏 SAISIE HT DIRECTE"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={styles.formRow}>
                                <div style={{ flex: 0.6 }}>
                                    <label style={styles.label}>Qté Achetée</label>
                                    <input type="number" style={styles.input} value={inputQte} onChange={e => setInputQte(e.target.value)} placeholder="0" />
                                </div>

                                {!isManualTax ? (
                                    <div style={{ flex: 1 }}>
                                        <label style={styles.label}>Montant TTC Facturé</label>
                                        <input type="number" style={{ ...styles.input, borderColor: '#059669' }} value={inputMontant} onChange={e => setInputMontant(e.target.value)} placeholder="Total TTC" />
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ flex: 1 }}>
                                            <label style={styles.label}>Montant HT</label>
                                            <input type="number" style={{ ...styles.input, borderColor: '#2563eb' }} value={inputHT} onChange={e => setInputHT(e.target.value)} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={styles.label}>Montant TVA</label>
                                            <input type="number" style={{ ...styles.input, borderColor: '#2563eb' }} value={inputTVA} onChange={e => setInputTVA(e.target.value)} />
                                        </div>
                                    </>
                                )}
                                
                                <div style={{ flex: 1 }}>
                                    <label style={styles.label}>Observations</label>
                                    <input style={styles.input} value={obs} onChange={e => setObs(e.target.value)} placeholder="Note..." />
                                </div>

                                <button style={{...styles.btnAdd, background: editingInterneId ? '#3b82f6' : '#1e293b'}} onClick={ajouterLigne}>
                                    <Plus size={18} /> {editingInterneId ? "METTRE À JOUR" : "AJOUTER"}
                                </button>
                            </div>

                            {selectedMp && (inputQte || inputMontant || inputHT) && (
                                <div style={{ marginBottom: '15px', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '800' }}>
                                        <span>IMPACT STOCK (HT): <strong style={{ color: '#0f172a' }}>{fmt(isManualTax ? inputHT : (isTVAApplicable && regimeTVA === 1 ? Number(inputMontant) / (1 + tauxTVA/100) : inputMontant))} F</strong></span>
                                        <span>P.A.U HT: <strong style={{ color: '#059669' }}>{inputQte > 0 ? fmt((isManualTax ? Number(inputHT) : (isTVAApplicable && regimeTVA === 1 ? Number(inputMontant) / (1 + tauxTVA/100) : inputMontant)) / Number(inputQte)) : 0} F</strong></span>
                                    </div>
                                </div>
                            )}

                            <div style={styles.inputGroup}>
                                <label style={{...styles.label, color: header.modeReglement ? '#475569' : '#ef4444'}}>
                                    MODE DE RÈGLEMENT * {!header.modeReglement && "(OBLIGATOIRE)"}
                                </label>
                                <select 
                                    style={{
                                        ...styles.input, 
                                        border: header.modeReglement ? '1px solid #cbd5e1' : '2px solid #ef4444',
                                        background: header.modeReglement ? '#fff' : '#fff1f2'
                                    }} 
                                    value={header.modeReglement} 
                                    onChange={e => {
                                        const nouveauMode = e.target.value;
                                        setHeader({
                                            ...header, 
                                            modeReglement: nouveauMode, 
                                            montantVersé: nouveauMode === 'ACOMPTE' ? header.montantVersé : 0 
                                        });
                                    }}
                                >
                                    <option value="">-- CHOISIR LE MODE --</option>
                                    <optgroup label="LOGIQUE SYSTÈME">
                                         <option value="CREDIT">CREDIT</option>
                                        <option value="ACOMPTE">ACOMPTE</option>
                                    </optgroup>
                                    <optgroup label="MOYENS DE PAIEMENT (COMPTANT)">
                                        {paymentMethods.map(m => (
                                            <option key={m.id} value={m.code}>
                                                {m.libelle.toUpperCase()}
                                            </option>
                                        ))}
                                    </optgroup>
                                </select>
                            </div>

                            {header.modeReglement === 'ACOMPTE' && (
                                <div style={{...styles.inputGroup, animation: 'slideIn 0.3s ease', marginTop: '10px'}}>
                                    <label style={{...styles.label, color: '#059669'}}>MONTANT VERSÉ (ACOMPTE) *</label>
                                    <input 
                                        type="number" 
                                        style={{...styles.input, border: '2px solid #059669', background: '#f0fdf4'}} 
                                        value={header.montantVersé} 
                                        onChange={e => setHeader({...header, montantVersé: e.target.value})} 
                                        placeholder="Saisir l'avance..."
                                    />
                                </div>
                            )}
                        </section>
                    </div>

                    <div style={{...styles.card, marginTop:'20px', padding:0, border: 'none'}}>
                        <div style={styles.tableHeader}>
                            <div style={{display:'flex', alignItems:'center', gap: '10px'}}><ShoppingBag size={20}/> <span>BORDEREAU DE RÉCEPTION</span></div>
                            <div style={styles.totalBadge}>TOTAL : {fmt(totalPanier)} FCFA</div>
                        </div>
                        {/* 🔥 CONTENEUR SCROLLABLE DU PANIER (limité exactement à ~5 lignes) */}
                        <div style={styles.tableScrollContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>ID ACHAT</th><th style={styles.th}>LOT ID</th><th style={styles.th}>N° FACTURE</th><th style={styles.th}>ID ART.</th>
                                        <th style={styles.th}>NOM ARTICLE</th><th style={styles.thCenter}>STK AV.</th><th style={styles.thCenter}>QTE ACH.</th><th style={styles.thCenter}>P.U HT</th>
                                        {regimeTVA === 1 && <th style={styles.thCenter}>MT HT</th>}
                                        {regimeTVA === 1 && <th style={styles.thCenter}>MT TVA</th>}
                                        <th style={styles.thCenter}>MT TTC</th><th style={styles.thCenter}>ECART CMP</th><th style={styles.th}>OBS.</th><th style={styles.th}>FOURNISSEUR</th><th style={styles.th}>DATE</th><th style={styles.thCenter}>ACTION</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {panier.length === 0 ? (
                                        <tr><td colSpan="16" style={styles.emptyMsg}>Aucun article dans le bordereau</td></tr>
                                    ) : panier.map(item => (
                                        <tr key={item.id_interne} style={{...styles.trBody, background: editingInterneId === item.id_interne ? '#eff6ff' : 'white'}}>
                                            <td style={styles.tdId}>{item.id_achat}</td>
                                            <td style={styles.tdId}>{item.lot_id}</td>
                                            <td style={styles.td}><strong>{item.num_facture}</strong></td>
                                            <td style={styles.tdId}>{item.matiere_id}</td>
                                            <td style={styles.td}><strong>{item.nom_matiere_snap}</strong></td>
                                            <td style={styles.tdCenter}>{item.stock_avant_achat}</td>
                                            <td style={{...styles.tdCenter, fontWeight:'bold'}}>{item.qte_achetee}</td>
                                            <td style={styles.tdCenter}>{fmt(item.prix_achat_unitaire)}</td>
                                            {regimeTVA === 1 && <td style={styles.tdCenter}>{fmt(item.montant_ht_ligne)}</td>}
                                            {regimeTVA === 1 && <td style={styles.tdCenter}>{fmt(item.montant_tva_ligne)}</td>}
                                            <td style={styles.tdPrice}>{fmt(item.montant_facture_ligne)}</td>
                                            <td style={{...styles.tdCenter, color: item.ecart > 0 ? '#ef4444' : '#059669', fontWeight:'bold'}}>
                                                {item.ecart > 0 ? `+${fmt(item.ecart)}` : fmt(item.ecart)}
                                            </td>
                                            <td style={styles.tdId}>{item.observations}</td>
                                            <td style={styles.td}>{item.nom_fournisseur_snap}</td>
                                            <td style={styles.tdId}>{item.date}</td>
                                            <td style={styles.tdCenter}>
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                                    <button style={styles.btnEdit} title="Modifier la ligne" onClick={() => preparerEditionLigne(item)}>
                                                        <Edit3 size={14}/>
                                                    </button>
                                                    <button style={styles.btnDel} title="Supprimer" onClick={() => supprimerLigne(item.id_interne)}>
                                                        <Trash2 size={14}/>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                {/* 🔥 LIGNE DES TOTAUX FIXÉE AU BAS DU TABLEAU (SCROLLABLE HORIZONTALEMENT AVEC LUI) */}
                                {panier.length > 0 && (
                                    <tfoot>
                                        <tr style={styles.trFooter}>
                                            <td colSpan="6" style={{...styles.tdFooter, textAlign: 'left'}}><strong>TOTAUX GÉNÉRAUX</strong></td>
                                            <td style={{...styles.tdFooter, textAlign: 'center'}}><strong>{fmt(totalQte)}</strong></td>
                                            <td style={styles.tdFooter}></td>
                                            {regimeTVA === 1 && <td style={{...styles.tdFooter, textAlign: 'center'}}><strong>{fmt(totalHT)} F</strong></td>}
                                            {regimeTVA === 1 && <td style={{...styles.tdFooter, textAlign: 'center'}}><strong>{fmt(totalTVA)} F</strong></td>}
                                            <td style={{...styles.tdFooter, textAlign: 'center', color: '#059669'}}><strong>{fmt(totalPanier)} F</strong></td>
                                            <td colSpan="5" style={styles.tdFooter}></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

const styles = {
    layout: { display: 'flex', height: '100vh', background: '#f1f5f9', fontFamily: '"Inter", sans-serif', overflow: 'hidden' },
    main: { flex: 1, height: '100vh', overflowY: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' },
    alertToastContainer: { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, minWidth: '400px' },
    alertToast: { padding: '12px 20px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' },
    headerBar: { background: '#fff', padding: '12px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #e2e8f0', flexShrink: 0 },
    title: { fontSize: '18px', fontWeight: '900', color: '#1e293b' },
    badgeContainer: { display: 'flex', gap: '10px', marginTop: '3px' },
    badge: { padding: '2px 8px', borderRadius: '4px', fontSize: '10px', background: '#f8fafc', border: '1px solid #cbd5e1', fontWeight: 'bold' },
    btnSave: { background: '#059669', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' },
    container: { padding: '20px 25px', flex: 1, overflowY: 'hidden', display: 'flex', flexDirection: 'column' },
    gridTop: { display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: '20px', flexShrink: 0 },
    card: { background: '#fff', borderRadius: '16px', padding: '15px 20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' },
    cardTitle: { fontSize: '12px', fontWeight: 'bold', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px', textTransform: 'uppercase' },
    searchContainer: { position: 'relative', marginBottom: '10px' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid #e2e8f0' },
    listArea: { height: '180px', overflowY: 'auto', borderRadius: '8px', border: '1px solid #f1f5f9' },
    listHeader: { display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' },
    listItem: { display: 'flex', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid #f8fafc', cursor: 'pointer', fontSize: '12px', alignItems: 'center' },
    stockLabel: { background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' },
    puLabel: { color: '#059669', fontSize: '10px', fontWeight: 'bold' },
    formRow: { display: 'flex', gap: '12px', marginBottom: '10px', alignItems: 'flex-end' },
    inputGroup: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', transition: 'all 0.3s ease' },
    label: { fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase' },
    input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' },
    selectionBox: { padding: '10px 15px', borderRadius: '8px', border: '1px dashed', marginBottom: '10px' },
    btnAdd: { padding: '10px 20px', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', fontSize: '13px' },
    tableHeader: { padding: '12px 20px', background: '#059669', color: '#fff', borderTopLeftRadius: '16px', borderTopRightRadius: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    totalBadge: { background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' },
    
    /* 🔥 CONTENEUR AVEC SCROLL VERTICAL LIMITÉ À 5 LIGNES (~275px) */
    tableScrollContainer: { maxHeight: '275px', overflowY: 'auto', overflowX: 'auto', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' },
    
    table: { width: '100%', borderCollapse: 'collapse', minWidth: '1600px', background: 'white' },
    th: { textAlign: 'left', padding: '12px 15px', fontSize: '11px', color: '#fff', background: '#047857', textTransform: 'uppercase', position: 'sticky', top: 0, zIndex: 2 },
    thCenter: { textAlign: 'center', padding: '12px 15px', fontSize: '11px', color: '#fff', background: '#047857', textTransform: 'uppercase', position: 'sticky', top: 0, zIndex: 2 },
    trBody: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: '12px 15px', fontSize: '12px' },
    tdId: { padding: '12px 15px', fontSize: '11px', color: '#64748b', fontFamily: 'monospace' },
    tdCenter: { padding: '12px 15px', textAlign: 'center', fontSize: '12px' },
    tdPrice: { padding: '12px 15px', textAlign: 'center', fontWeight: 'bold', color: '#059669', fontSize: '12px' },
    
    /* 🔥 STYLE POUR LE PIED DE TABLEAU DES TOTAUX */
    trFooter: { background: '#f8fafc', borderTop: '2px solid #cbd5e1', position: 'sticky', bottom: 0, zIndex: 1 },
    tdFooter: { padding: '12px 15px', fontSize: '12px', color: '#0f172a', background: '#f8fafc' },

    btnEdit: { background: '#eff6ff', color: '#3b82f6', border: 'none', padding: '6px', borderRadius: '6px', cursor: 'pointer' },
    btnDel: { background: '#fee2e2', color: '#ef4444', border: 'none', padding: '6px', borderRadius: '6px', cursor: 'pointer' },
    emptyMsg: { textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '13px' }
};

export default AchatMp;